// Authentic Multi-Board Ludo Party Battle Server Engine (2P, 3P, 4P, 5P, 6P Classic Boards)
// Featuring:
// - 3 Sixes in a Row Rule (3rd six canceled & turn skipped)
// - Stacking Extra Rolls (6 + Kill = 2 extra rolls, 6 + Home = 2 extra rolls, Kill = 1, Home = 1, 6 = 1)
// - Delayed move execution so die stops rolling before tokens move
// - Pre-game custom color, pawn style selection, and locks options once game starts.

export const LUDO_BOARDS = {
  '2P': {
    type: '2P',
    name: '2 Players (Duel)',
    colors: ['red', 'yellow'],
    pathLength: 52,
    homeStretchLength: 5,
    homePos: 56,
    startOffsets: { red: 0, yellow: 26 },
    safeSteps: [0, 8, 13, 21, 26, 34, 39, 47]
  },
  '3P': {
    type: '3P',
    name: '3 Players (Triangle)',
    colors: ['red', 'green', 'yellow'],
    pathLength: 52,
    homeStretchLength: 5,
    homePos: 56,
    startOffsets: { red: 0, green: 13, yellow: 26 },
    safeSteps: [0, 8, 13, 21, 26, 34, 39, 47]
  },
  '4P': {
    type: '4P',
    name: '4 Players (Classic 15x15)',
    colors: ['red', 'green', 'yellow', 'blue'],
    pathLength: 52,
    homeStretchLength: 5,
    homePos: 56,
    startOffsets: { red: 0, green: 13, yellow: 26, blue: 39 },
    safeSteps: [0, 8, 13, 21, 26, 34, 39, 47]
  },
  '5P': {
    type: '5P',
    name: '5 Players (Classic 5P)',
    colors: ['red', 'green', 'yellow', 'blue', 'purple'],
    pathLength: 65,
    homeStretchLength: 5,
    homePos: 70,
    startOffsets: { red: 0, green: 13, yellow: 26, blue: 39, purple: 52 },
    safeSteps: [0, 8, 13, 21, 26, 34, 39, 47, 52, 60]
  },
  '6P': {
    type: '6P',
    name: '6 Players (Classic 6P)',
    colors: ['red', 'green', 'yellow', 'blue', 'orange', 'purple'],
    pathLength: 78,
    homeStretchLength: 5,
    homePos: 83,
    startOffsets: { red: 0, green: 13, yellow: 26, blue: 39, orange: 52, purple: 65 },
    safeSteps: [0, 8, 13, 21, 26, 34, 39, 47, 52, 60, 65, 73]
  }
};

export class LudoEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.state = 'LOBBY'; // 'LOBBY' | 'IN_GAME'
    this.matchSize = '4P';
    this.boardConfig = LUDO_BOARDS['4P'];
    this.playerCustomizations = {}; // playerId -> { color, pawnStyle }
    this.playerMap = {};
    this.currentTurnIndex = 0;
    this.currentDice = 1;
    this.canRoll = true;
    this.canMove = false;
    this.movableTokens = [];
    this.extraRollsRemaining = 0;

    const pCount = this.room.players?.length || 1;
    if (pCount === 2) this.matchSize = '2P';
    else if (pCount === 3) this.matchSize = '3P';
    else if (pCount === 5) this.matchSize = '5P';
    else if (pCount >= 6) this.matchSize = '6P';
    else this.matchSize = '4P';

    this.boardConfig = LUDO_BOARDS[this.matchSize];
  }

  start() {
    this.state = 'LOBBY';
    this.emitLobbySync();
  }

  emitLobbySync() {
    this.io.to(this.room.code).emit('ludo_lobbySync', {
      state: this.state,
      matchSize: this.matchSize,
      boardConfig: this.boardConfig,
      customizations: this.playerCustomizations,
      players: this.room.players || [],
      hostId: this.room.hostId
    });
  }

  setMatchSize(newSize) {
    if (this.state !== 'LOBBY') return;
    if (!LUDO_BOARDS[newSize]) return;
    this.matchSize = newSize;
    this.boardConfig = LUDO_BOARDS[newSize];
    this.emitLobbySync();
  }

  customizePlayer(playerId, { color, pawnStyle }) {
    if (this.state !== 'LOBBY') return;
    if (!this.playerCustomizations[playerId]) {
      this.playerCustomizations[playerId] = {};
    }
    if (color) this.playerCustomizations[playerId].color = color;
    if (pawnStyle) this.playerCustomizations[playerId].pawnStyle = pawnStyle;
    this.emitLobbySync();
  }

  enterGame(playerId) {
    if (playerId !== this.room.hostId && this.room.players?.length > 1) {
      const isHost = this.room.players?.find(p => p.id === playerId)?.isHost;
      if (!isHost) return;
    }

    if (this.state === 'IN_GAME') return;
    this.state = 'IN_GAME';

    this.playerMap = {};
    this.boardConfig = LUDO_BOARDS[this.matchSize] || LUDO_BOARDS['4P'];
    const neededSeats = this.boardConfig.colors.length;
    const availableColors = [...this.boardConfig.colors];

    const humanPlayers = (this.room.players || []).slice(0, neededSeats);

    // 1. Assign human players
    humanPlayers.forEach((p, idx) => {
      const custom = this.playerCustomizations[p.id] || {};
      let chosenColor = custom.color;

      if (!chosenColor || !availableColors.includes(chosenColor)) {
        chosenColor = availableColors[0];
      }
      availableColors.splice(availableColors.indexOf(chosenColor), 1);

      this.playerMap[p.id] = {
        id: p.id,
        name: p.name,
        color: chosenColor,
        pawnStyle: custom.pawnStyle || 'toy',
        tokens: [-1, -1, -1, -1],
        score: 0,
        kills: 0,
        deaths: 0,
        isFinished: false,
        rank: null,
        hasLeft: false,
        isBot: !!p.isBot,
        consecutiveSixes: 0
      };
    });

    // 2. Auto-fill bots if needed
    const botStyles = ['toy', 'crown', 'rocket', 'gem', 'chibi'];
    const botNames = {
      orange: 'Orange Fox 🤖',
      red: 'Red Raider 🤖',
      yellow: 'Yellow Flash 🤖',
      blue: 'Blue Sonic 🤖',
      green: 'Green Goblin 🤖',
      purple: 'Purple Mage 🤖'
    };

    while (availableColors.length > 0) {
      const botColor = availableColors.shift();
      const botId = `bot_${botColor}_${Date.now().toString().slice(-4)}`;
      const randomStyle = botStyles[Math.floor(Math.random() * botStyles.length)];

      this.playerMap[botId] = {
        id: botId,
        name: botNames[botColor] || `Bot ${botColor.toUpperCase()} 🤖`,
        color: botColor,
        pawnStyle: randomStyle,
        tokens: [-1, -1, -1, -1],
        score: 0,
        kills: 0,
        deaths: 0,
        isFinished: false,
        rank: null,
        hasLeft: false,
        isBot: true,
        consecutiveSixes: 0
      };
    }

    this.finishedPlayers = [];

    this.currentTurnIndex = 0;
    this.canRoll = true;
    this.canMove = false;
    this.extraRollsRemaining = 0;

    this.io.to(this.room.code).emit('ludo_started', {
      state: 'IN_GAME',
      matchSize: this.matchSize,
      boardType: this.matchSize,
      boardConfig: this.boardConfig,
      players: this.playerMap,
      currentTurnId: this.getCurrentPlayer()?.id,
      currentDice: this.currentDice
    });

    this.checkIfBotTurn();
  }

  getCurrentPlayer() {
    const playerList = Object.values(this.playerMap);
    if (playerList.length === 0) return null;
    return playerList[this.currentTurnIndex % playerList.length];
  }

  checkIfBotTurn() {
    const cp = this.getCurrentPlayer();
    if (cp && cp.isBot && this.canRoll && this.state === 'IN_GAME') {
      setTimeout(() => this.rollDice(cp.id), 900);
    }
  }

  rollDice(playerId) {
    if (this.state !== 'IN_GAME') return;
    const cp = this.getCurrentPlayer();
    if (!cp || cp.id !== playerId || !this.canRoll) return;

    this.currentDice = Math.floor(Math.random() * 6) + 1;
    this.canRoll = false;

    const pState = this.playerMap[playerId];

    // ==========================================
    // RULE: 3 Consecutive Sixes -> Turn Forfeited!
    // ==========================================
    if (this.currentDice === 6) {
      pState.consecutiveSixes = (pState.consecutiveSixes || 0) + 1;
      if (pState.consecutiveSixes === 3) {
        pState.consecutiveSixes = 0;
        this.extraRollsRemaining = 0;
        this.io.to(this.room.code).emit('ludo_diceRolled', {
          playerId,
          dice: 6,
          movableTokens: [],
          forfeited: true,
          notice: '⚠️ 3 consecutive 6s! Turn forfeited!'
        });
        setTimeout(() => this.nextTurn(), 1400);
        return;
      }
    } else {
      pState.consecutiveSixes = 0;
    }

    this.movableTokens = this.getMovableTokens(pState, this.currentDice);

    this.io.to(this.room.code).emit('ludo_diceRolled', {
      playerId,
      dice: this.currentDice,
      movableTokens: this.movableTokens
    });

    if (this.movableTokens.length === 0) {
      // If rolled 6 with no moves, but has extra roll, allow extra roll; else next turn
      if (this.currentDice === 6 && this.extraRollsRemaining > 0) {
        this.extraRollsRemaining--;
        this.canRoll = true;
        setTimeout(() => {
          this.io.to(this.room.code).emit('ludo_extraTurn', {
            playerId,
            reason: '🎲 ROLLED A 6 (EXTRA ROLL)!',
            remainingExtra: this.extraRollsRemaining
          });
          this.checkIfBotTurn();
        }, 1200);
      } else {
        setTimeout(() => this.nextTurn(), 1400);
      }
    } else if (this.movableTokens.length === 1 || cp.isBot) {
      const chosenToken = cp.isBot
        ? (this.movableTokens.find(t => pState.tokens[t] === -1) ?? this.movableTokens[0])
        : this.movableTokens[0];
      // Generous delay so client 3D dice finishes rolling before token begins moving!
      setTimeout(() => this.moveToken(playerId, chosenToken), cp.isBot ? 1400 : 1200);
    } else {
      this.canMove = true;
    }
  }

  getMovableTokens(pState, dice) {
    const movable = [];
    const maxPos = this.boardConfig.homePos;

    pState.tokens.forEach((pos, idx) => {
      if (pos === -1) {
        if (dice === 6) movable.push(idx);
      } else if (pos + dice <= maxPos) {
        movable.push(idx);
      }
    });
    return movable;
  }

  moveToken(playerId, tokenIndex) {
    if (this.state !== 'IN_GAME') return;
    const cp = this.getCurrentPlayer();
    if (!cp || cp.id !== playerId) return;

    const pState = this.playerMap[playerId];
    if (!pState) return;

    const curPos = pState.tokens[tokenIndex];
    const maxPos = this.boardConfig.homePos;

    if (curPos === -1 && this.currentDice === 6) {
      pState.tokens[tokenIndex] = 0;
    } else if (curPos >= 0 && curPos + this.currentDice <= maxPos) {
      pState.tokens[tokenIndex] += this.currentDice;
    } else {
      return;
    }

    this.canMove = false;

    const newPos = pState.tokens[tokenIndex];
    const reachedHome = (newPos === maxPos);
    let knocked = null;

    const mainTrackLimit = this.boardConfig.pathLength - 2;
    if (newPos > 0 && newPos <= mainTrackLimit) {
      const myBoardIndex = (newPos + this.boardConfig.startOffsets[pState.color]) % this.boardConfig.pathLength;
      const isSafe = this.boardConfig.safeSteps.includes(myBoardIndex);

      if (!isSafe) {
        for (const otherId in this.playerMap) {
          if (otherId === playerId) continue;
          const other = this.playerMap[otherId];
          other.tokens.forEach((opos, oidx) => {
            if (opos > 0 && opos <= mainTrackLimit) {
              const otherBoardIndex = (opos + this.boardConfig.startOffsets[other.color]) % this.boardConfig.pathLength;
              if (otherBoardIndex === myBoardIndex) {
                other.tokens[oidx] = -1;
                knocked = { id: otherId, name: other.name, color: other.color, tokenIndex: oidx };
                pState.kills = (pState.kills || 0) + 1;
                other.deaths = (other.deaths || 0) + 1;
              }
            }
          });
        }
      }
    }

    this.io.to(this.room.code).emit('ludo_tokenMoved', {
      playerId,
      tokenIndex,
      oldPos: curPos,
      newPos,
      dice: this.currentDice,
      players: this.playerMap,
      knocked,
      reachedHome
    });

    // Multi-Player Finish: Check if this player finished all 4 tokens
    if (pState.tokens.every((pos) => pos === maxPos) && !pState.isFinished) {
      pState.isFinished = true;
      if (!this.finishedPlayers) this.finishedPlayers = [];
      this.finishedPlayers.push(pState);
      pState.rank = this.finishedPlayers.length;

      this.io.to(this.room.code).emit('ludo_playerFinished', {
        playerId,
        name: pState.name,
        color: pState.color,
        rank: pState.rank
      });

      // Check if all N-1 players have finished
      const activeRemaining = Object.values(this.playerMap).filter(p => !p.isFinished && !p.hasLeft);
      if (activeRemaining.length <= 1) {
        if (activeRemaining.length === 1) {
          const lastP = activeRemaining[0];
          lastP.isFinished = true;
          this.finishedPlayers.push(lastP);
          lastP.rank = this.finishedPlayers.length;
          this.io.to(this.room.code).emit('ludo_playerFinished', {
            playerId: lastP.id,
            name: lastP.name,
            color: lastP.color,
            rank: lastP.rank
          });
        }

        const leaderboard = Object.values(this.playerMap).map(p => ({
          id: p.id,
          name: p.name,
          color: p.color,
          pawnStyle: p.pawnStyle,
          rank: p.rank || Object.keys(this.playerMap).length,
          kills: p.kills || 0,
          deaths: p.deaths || 0,
          tokensHome: p.tokens.filter(t => t === maxPos).length,
          hasLeft: !!p.hasLeft
        })).sort((a, b) => a.rank - b.rank);

        this.state = 'GAME_OVER';
        setTimeout(() => {
          this.io.to(this.room.code).emit('ludo_gameOver', { leaderboard });
        }, 1200);
        return;
      }

      // If active players remain, advance to next turn
      const delayMs = Math.max(1000, (newPos - (curPos === -1 ? -1 : curPos)) * 140 + 300);
      setTimeout(() => this.nextTurn(), delayMs);
      return;
    }

    // ==========================================
    // RULE: Extra Rolls Stacking
    // 6 = +1, Kill = +1, Reached Home = +1
    // (6 + Kill = +2 extra rolls, 6 + Home = +2 extra rolls)
    // ==========================================
    let bonusRolls = 0;
    if (this.currentDice === 6) {
      bonusRolls += 1;
    }
    if (knocked) {
      bonusRolls += 1;
    }
    if (reachedHome) {
      bonusRolls += 1;
    }

    this.extraRollsRemaining = (this.extraRollsRemaining || 0) + bonusRolls;

    if (this.extraRollsRemaining > 0) {
      this.extraRollsRemaining--;
      this.canRoll = true;

      let reason = '🎲 EXTRA ROLL!';
      if (this.currentDice === 6 && knocked) reason = '💥 6 + KILL COMBO (+2 EXTRA ROLLS)!';
      else if (this.currentDice === 6 && reachedHome) reason = '🏁 6 + HOME REACHED (+2 EXTRA ROLLS)!';
      else if (knocked) reason = '💥 TOKEN KILLED (+1 EXTRA ROLL)!';
      else if (reachedHome) reason = '🏁 TOKEN IN HOME (+1 EXTRA ROLL)!';
      else if (this.currentDice === 6) reason = '🎲 ROLLED A 6 (+1 EXTRA ROLL)!';

      const delayMs = Math.max(1200, (newPos - (curPos === -1 ? -1 : curPos)) * 140 + 400);

      setTimeout(() => {
        this.io.to(this.room.code).emit('ludo_extraTurn', {
          playerId,
          reason,
          remainingExtra: this.extraRollsRemaining
        });
        this.checkIfBotTurn();
      }, delayMs);
    } else {
      const delayMs = Math.max(1000, (newPos - (curPos === -1 ? -1 : curPos)) * 140 + 300);
      setTimeout(() => this.nextTurn(), delayMs);
    }
  }

  nextTurn() {
    this.extraRollsRemaining = 0;
    this.canRoll = true;
    this.canMove = false;
    this.movableTokens = [];

    const allPlayers = Object.values(this.playerMap);
    if (allPlayers.length === 0) return;

    let count = 0;
    do {
      this.currentTurnIndex = (this.currentTurnIndex + 1) % allPlayers.length;
      count++;
      const cp = this.getCurrentPlayer();
      if (cp && !cp.isFinished && !cp.hasLeft) {
        break;
      }
    } while (count <= allPlayers.length * 2);

    this.io.to(this.room.code).emit('ludo_turnChange', {
      currentTurnId: this.getCurrentPlayer()?.id
    });

    this.checkIfBotTurn();
  }

  handleLeaveMatch(playerId) {
    const pState = this.playerMap[playerId];
    if (!pState) return;

    pState.hasLeft = true;
    this.io.to(this.room.code).emit('ludo_playerLeft', {
      playerId,
      name: pState.name
    });

    const activeRemaining = Object.values(this.playerMap).filter(p => !p.isFinished && !p.hasLeft);
    if (activeRemaining.length <= 1) {
      if (activeRemaining.length === 1) {
        const lastP = activeRemaining[0];
        lastP.isFinished = true;
        if (!this.finishedPlayers) this.finishedPlayers = [];
        this.finishedPlayers.push(lastP);
        lastP.rank = this.finishedPlayers.length;
      }

      const leaderboard = Object.values(this.playerMap).map(p => ({
        id: p.id,
        name: p.name,
        color: p.color,
        pawnStyle: p.pawnStyle,
        rank: p.rank || Object.keys(this.playerMap).length,
        kills: p.kills || 0,
        deaths: p.deaths || 0,
        tokensHome: p.tokens.filter(t => t === this.boardConfig.homePos).length,
        hasLeft: !!p.hasLeft
      })).sort((a, b) => a.rank - b.rank);

      this.state = 'GAME_OVER';
      this.io.to(this.room.code).emit('ludo_gameOver', { leaderboard });
      return;
    }

    if (this.getCurrentPlayer()?.id === playerId) {
      this.nextTurn();
    }
  }

  destroy() {
    this.playerMap = {};
  }
}
